import { useState } from "react";
import { Hourglass, Save } from "lucide-react";
import { differenceInCalendarDays, parseISO, subDays } from "date-fns";
import { toast } from "sonner";
import { useCollection, usePrefs } from "@/hooks/useCollection";
import { rupiah, sum, iso, fmtDate } from "@/lib/format";
import { calcJaminan } from "@/lib/jaminan";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Section, Stat } from "./shared";
import { Field } from "./JaminanTab";

const EMPTY = { end: "", guests_per_day: 0, tip_per_guest: 0, daily_expense: 0 };

const Line = ({ label, value, tone = "text-gray-700", testid }) => (
  <div className="flex justify-between gap-3 text-sm py-1.5">
    <span className="text-gray-500">{label}</span>
    <span className={`font-semibold whitespace-nowrap ${tone}`} data-testid={testid}>{value}</span>
  </div>
);

export default function KontrakTab() {
  const { prefs, save } = usePrefs();
  if (!prefs) return <div className="h-40 rounded-3xl bg-pink-100/60 animate-pulse" />;
  return <KontrakForm prefs={prefs} save={save} />;
}

function KontrakForm({ prefs, save }) {
  const { items: incomes } = useCollection("incomes");
  const { items: expenses } = useCollection("expenses");
  const [v, setV] = useState(() => ({ ...EMPTY, ...prefs.kontrak }));

  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const since = iso(subDays(new Date(), 30));
  const avgTipsDay = sum(incomes.filter((x) => x.type === "tips" && x.date >= since)) / 30;
  const avgExpDay = sum(expenses.filter((x) => x.date >= since)) / 30;

  const daysLeft = v.end ? Math.max(differenceInCalendarDays(parseISO(v.end), new Date()), 0) : 0;
  const quota = prefs?.off_quota ?? 3;
  const offDays = Math.min(Math.round((daysLeft / 30.44) * quota), daysLeft);
  const workDays = daysLeft - offDays;
  const tips = Number(v.guests_per_day) * Number(v.tip_per_guest) * workDays;
  const spend = Number(v.daily_expense) * daysLeft;
  const jam = calcJaminan(prefs?.jaminan, v.end || undefined);
  const total = jam.total + tips - spend;


  const submit = async () => {
    try {
      await save({ kontrak: { end: v.end, guests_per_day: Number(v.guests_per_day) || 0, tip_per_guest: Number(v.tip_per_guest) || 0, daily_expense: Number(v.daily_expense) || 0 } });
      toast.success("Kalkulator habis kontrak disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="duitku-kontrak">
      <div className="card-soft bg-gradient-to-br from-pink-400 to-rose-400 !border-0 text-white">
        <p className="text-sm opacity-90">Perkiraan uangku saat habis kontrak{v.end ? ` (${fmtDate(v.end)})` : ""}</p>
        <p className="font-heading text-4xl font-semibold mt-1" data-testid="kontrak-total">{rupiah(total)}</p>
        <p className="text-sm opacity-90 mt-2">{daysLeft} hari lagi · {workDays} hari kerja · {offDays} hari libur</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title={<span className="flex items-center gap-2"><Hourglass className="w-5 h-5 text-pink-400" /> Kalkulator uang saat habis kontrak</span>}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Tanggal habis kontrak"><input className="field" type="date" value={v.end} onChange={set("end")} data-testid="kontrak-end" /></Field>
            <Field label="Perkiraan tamu per hari"><input className="field" type="number" min="0" value={v.guests_per_day} onChange={set("guests_per_day")} data-testid="kontrak-guests" /></Field>
            <Field label="Tips rata-rata per tamu (Rp)" hint={avgTipsDay > 0 ? `Tips harianmu 30 hari terakhir: ${rupiah(avgTipsDay)}/hari` : undefined}><input className="field" type="number" min="0" value={v.tip_per_guest} onChange={set("tip_per_guest")} data-testid="kontrak-tip" /></Field>
            <Field label="Perkiraan pengeluaran harian (Rp)" hint={avgExpDay > 0 ? `Rata-rata 30 hari terakhir: ${rupiah(avgExpDay)}/hari` : undefined}>
              <input className="field" type="number" min="0" value={v.daily_expense} onChange={set("daily_expense")} data-testid="kontrak-daily-expense" />
              {avgExpDay > 0 && <button type="button" className="text-[11px] text-pink-500 underline" onClick={() => setV((p) => ({ ...p, daily_expense: Math.round(avgExpDay) }))} data-testid="kontrak-use-avg-expense">Pakai rata-rata</button>}
            </Field>
          </div>
          <p className="text-[11px] text-gray-400 mt-3">Hari kerja dihitung dari sisa hari dikurangi jatah libur {quota} hari/bulan (ubah di Akunku).</p>
          <Button onClick={submit} className="rounded-full h-11 mt-4" data-testid="kontrak-save"><Save className="w-4 h-4 mr-2" /> Simpan</Button>
        </Section>
        <Section title="Rincian">
          <Line label={`Uang jaminan (s/d ${fmtDate(jam.end)})`} value={rupiah(jam.deposit + jam.depositMonthly)} tone="text-sky-600" testid="kontrak-jaminan" />
          <Line label={`Tabungan potongan gaji (${jam.months} bulan)`} value={rupiah(jam.saving)} tone="text-pink-500" testid="kontrak-saving" />
          <Line label={`Tips: ${v.guests_per_day || 0} tamu x ${rupiah(v.tip_per_guest)} x ${workDays} hari`} value={`+${rupiah(tips)}`} tone="text-emerald-500" testid="kontrak-tips" />
          <Line label={`Pengeluaran: ${rupiah(v.daily_expense)} x ${daysLeft} hari`} value={`-${rupiah(spend)}`} tone="text-rose-500" testid="kontrak-spend" />
          <div className="border-t border-pink-100 mt-2 pt-2"><Line label="Total" value={rupiah(total)} tone="text-gray-800" testid="kontrak-total-line" /></div>
          {!prefs?.jaminan?.start && <p className="text-xs text-amber-600 mt-3" data-testid="kontrak-need-jaminan">Isi dulu tanggal mulai kerja di tab Uang Jaminan supaya jaminan & tabungan ikut terhitung.</p>}
        </Section>
      </div>
    </div>
  );
}
